import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useParams, Link } from "react-router-dom";
import { SlidersHorizontal, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { ProductCard } from "@/components/marketplace/ProductCard";
import { SectionHeader } from "@/components/marketplace/SectionHeader";
import { LoadingState, EmptyState } from "@/components/common/States";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const SORTS = [
  ["popular", "Popularity"],
  ["price_low", "Price: Low to High"],
  ["price_high", "Price: High to Low"],
  ["rating", "Rating"],
  ["newest", "Newest"],
  ["featured", "Featured"],
  ["relevance", "Relevance"],
];

export default function Shop() {
  const { slug } = useParams(); // category route
  const [searchParams, setSearchParams] = useSearchParams();
  const { b2bMode } = useStore();
  const search = searchParams.get("search") || "";

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState(null);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("popular");
  const [category, setCategory] = useState(slug || "");
  const [b2bOnly, setB2bOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [catInfo, setCatInfo] = useState(null);
  const [facets, setFacets] = useState(null);
  const [filters, setFilters] = useState({ brand: "", vendor: "", availability: "", min_price: "", max_price: "", attrs: {} });
  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
  const toggleAttr = (key, value) => { setFilters((f) => ({ ...f, attrs: { ...f.attrs, [key]: f.attrs[key] === value ? "" : value } })); setPage(1); };
  const clearFilters = () => { setFilters({ brand: "", vendor: "", availability: "", min_price: "", max_price: "", attrs: {} }); setPage(1); };

  useEffect(() => {
    const q = new URLSearchParams(); if (category) q.set("category", category); if (search) q.set("search", search);
    api.get(`/products/filters?${q}`).then((r) => setFacets(r.data)).catch(() => setFacets(null));
  }, [category, search]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    setCategory(slug || "");
    setPage(1);
  }, [slug]);

  useEffect(() => {
    if (category) api.get(`/categories/${category}`).then((r) => setCatInfo(r.data)).catch(() => setCatInfo(null));
    else setCatInfo(null);
  }, [category]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (search) params.set("search", search);
    if (b2bOnly || b2bMode) params.set("b2b", "true");
    params.set("sort", sort);
    params.set("page", page);
    params.set("limit", 12);
    if (filters.brand) params.set("brand", filters.brand);
    if (filters.vendor) params.set("vendor", filters.vendor);
    if (filters.availability) params.set("availability", filters.availability);
    if (filters.min_price) params.set("min_price", filters.min_price);
    if (filters.max_price) params.set("max_price", filters.max_price);
    const attrPairs = Object.entries(filters.attrs).filter(([, v]) => v).map(([k, v]) => `${k}:${v}`);
    if (attrPairs.length) params.set("attr", attrPairs.join(","));
    setProducts(null);
    api.get(`/products?${params.toString()}`).then((r) => {
      setProducts(r.data.items);
      setTotal(r.data.total);
      setPages(r.data.pages);
    });
  }, [category, search, sort, page, b2bOnly, b2bMode, filters]);

  const title = catInfo ? catInfo.name : search ? `Results for "${search}"` : "All Products";
  const subtitle = catInfo?.description || `${total} products available`;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <nav className="flex items-center gap-1 text-xs text-stone-500 mb-4">
        <Link to="/" className="hover:text-secondary">Home</Link>
        <ChevronRight size={12} />
        <Link to="/shop" className="hover:text-secondary">Shop</Link>
        {catInfo && (<><ChevronRight size={12} /><span className="text-stone-800">{catInfo.name}</span></>)}
      </nav>

      <SectionHeader eyebrow="Marketplace" title={title} subtitle={subtitle} className="mb-6" />

      <div className="flex items-center justify-between gap-3 mb-6">
        <button
          data-testid="toggle-filters-btn"
          onClick={() => setShowFilters((s) => !s)}
          className="lg:hidden inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium"
        >
          <SlidersHorizontal size={16} /> Filters
        </button>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm text-stone-500 hidden sm:inline">Sort by</span>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-48" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SORTS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`sort-${v}`}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-8">
        {/* Filters */}
        <aside className={`${showFilters ? "block" : "hidden"} lg:block`} data-testid="filters-sidebar">
          <div className="rounded-xl border border-border bg-card p-5 sticky top-40">
            <h3 className="font-semibold text-stone-900 mb-3">Categories</h3>
            <div className="space-y-1">
              <button
                onClick={() => { setCategory(""); setPage(1); }}
                data-testid="filter-cat-all"
                className={`block w-full text-left text-sm py-1.5 px-2 rounded-md ${!category ? "bg-accent text-secondary font-semibold" : "text-stone-600 hover:bg-muted"}`}
              >
                All Categories
              </button>
              {categories.map((c) => (
                <button
                  key={c.slug}
                  onClick={() => { setCategory(c.slug); setPage(1); }}
                  data-testid={`filter-cat-${c.slug}`}
                  className={`block w-full text-left text-sm py-1.5 px-2 rounded-md ${category === c.slug ? "bg-accent text-secondary font-semibold" : "text-stone-600 hover:bg-muted"}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
            <div className="mt-5 pt-5 border-t border-border">
              <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                <Checkbox checked={b2bOnly} onCheckedChange={(v) => { setB2bOnly(!!v); setPage(1); }} data-testid="filter-b2b-only" />
                B2B Wholesale available
              </label>
            </div>
          </div>
          {facets && (
            <div className="mt-6 space-y-5 text-sm" data-testid="dynamic-filters">
              <div>
                <div className="flex items-center justify-between mb-2"><h3 className="font-semibold text-stone-900">Price (₹)</h3><button onClick={clearFilters} data-testid="filters-clear" className="text-xs text-secondary font-semibold">Clear all</button></div>
                <div className="flex gap-2"><input data-testid="filter-min-price" type="number" placeholder={`Min ${Math.floor(facets.price.min || 0)}`} value={filters.min_price} onChange={(e) => setFilter("min_price", e.target.value)} className="w-full rounded-lg border border-border px-2 py-1.5 text-sm" /><input data-testid="filter-max-price" type="number" placeholder={`Max ${Math.ceil(facets.price.max || 0)}`} value={filters.max_price} onChange={(e) => setFilter("max_price", e.target.value)} className="w-full rounded-lg border border-border px-2 py-1.5 text-sm" /></div>
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 mb-2">Availability</h3>
                {[["", "All"], ["in_stock", `In stock (${facets.availability.in_stock || 0})`], ["out_of_stock", `Out of stock (${facets.availability.out_of_stock || 0})`]].map(([v, l]) => (
                  <button key={v} onClick={() => setFilter("availability", v)} data-testid={`filter-avail-${v || "all"}`} className={`block w-full text-left px-2 py-1 rounded ${filters.availability === v ? "bg-accent text-secondary font-semibold" : "text-stone-600 hover:bg-muted"}`}>{l}</button>
                ))}
              </div>
              {facets.vendors.length > 0 && <div>
                <h3 className="font-semibold text-stone-900 mb-2">Vendor</h3>
                {facets.vendors.map((v) => <button key={v.slug} onClick={() => setFilter("vendor", filters.vendor === v.slug ? "" : v.slug)} data-testid={`filter-vendor-${v.slug}`} className={`block w-full text-left px-2 py-1 rounded ${filters.vendor === v.slug ? "bg-accent text-secondary font-semibold" : "text-stone-600 hover:bg-muted"}`}>{v.name} <span className="text-xs text-stone-400">({v.count})</span></button>)}
              </div>}
              {facets.brands.length > 0 && <div>
                <h3 className="font-semibold text-stone-900 mb-2">Brand</h3>
                {facets.brands.map((b) => <button key={b.value} onClick={() => setFilter("brand", filters.brand === b.value ? "" : b.value)} data-testid={`filter-brand-${b.value.replace(/\s+/g, "-").toLowerCase()}`} className={`block w-full text-left px-2 py-1 rounded ${filters.brand === b.value ? "bg-accent text-secondary font-semibold" : "text-stone-600 hover:bg-muted"}`}>{b.value} <span className="text-xs text-stone-400">({b.count})</span></button>)}
              </div>}
              {facets.attributes.map((a) => (
                <div key={a.key}>
                  <h3 className="font-semibold text-stone-900 mb-2">{a.key}</h3>
                  <div className="flex flex-wrap gap-1.5">{a.values.slice(0, 12).map((v) => <button key={v.value} onClick={() => toggleAttr(a.key, v.value)} data-testid={`filter-attr-${a.key}-${String(v.value).replace(/\s+/g, "-").toLowerCase()}`} className={`rounded-full border px-2.5 py-0.5 text-xs ${filters.attrs[a.key] === v.value ? "bg-secondary text-white border-secondary" : "border-border text-stone-600"}`}>{String(v.value)} ({v.count})</button>)}</div>
                </div>
              ))}
            </div>
          )}
        </aside>

        {/* Grid */}
        <div>
          {products === null ? (
            <LoadingState />
          ) : products.length === 0 ? (
            <EmptyState title="No products found" message="Try a different category or search term." />
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                {products.map((p) => <ProductCard key={p.slug} product={p} />)}
              </div>
              {pages > 1 && (
                <div className="mt-8 flex items-center justify-center gap-2" data-testid="pagination">
                  {Array.from({ length: pages }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      data-testid={`page-${i + 1}`}
                      className={`h-9 w-9 rounded-lg text-sm font-medium ${page === i + 1 ? "bg-secondary text-white" : "border border-border text-stone-700 hover:bg-muted"}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
